import * as React from "react"
import { router } from "@inertiajs/react"
import AppLayout from "@/components/AppLayout"
import PageTitle from "@/components/PageTitle"
import { Button } from "@/components/ui/Button"
import { Input } from "@/components/ui/Input"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/ToggleGroup"
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/Table"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/Select"
import { warehouse_path } from "@/lib/routes"
import { useMarket } from "@/lib/useMarket"
import CreateWarehouseDialog from "./components/CreateWarehouseDialog"
import WarehouseRow from "./components/WarehouseRow"

interface Warehouse {
  id: string
  external_id: number
  name: string
  code: string | null
  enabled: boolean
  org_node_id: string | null
  market_node_id: string | null
  sale_channel_node_ids: string[]
}

export interface OrgNode {
  id: string
  parent_id: string | null
  name: string
  depth: number
  sales_channel: boolean
}

interface Props {
  warehouses: Warehouse[]
  org_nodes: OrgNode[]
  can: { create: boolean }
}

function nodeLabel(node: OrgNode, byId: Record<string, OrgNode>): string {
  const path: string[] = [node.name]
  let cur: OrgNode | undefined = node
  while (cur && cur.parent_id) {
    cur = byId[cur.parent_id]
    if (cur) path.unshift(cur.name)
  }
  return path.join(" › ")
}

export default function WarehousesIndex({ warehouses: initial, org_nodes, can }: Props) {
  const market = useMarket()
  const [warehouses, setWarehouses] = React.useState(initial)
  React.useEffect(() => {
    setWarehouses(initial)
  }, [initial])
  const [query, setQuery] = React.useState("")
  const [tab, setTab] = React.useState<"all" | "allocated">("allocated")
  const [channelFilter, setChannelFilter] = React.useState<string>("all")
  const [savingId, setSavingId] = React.useState<string | null>(null)
  const [createOpen, setCreateOpen] = React.useState(false)

  const nodesById = React.useMemo(
    () => Object.fromEntries(org_nodes.map((n) => [n.id, n])),
    [org_nodes]
  )
  const salesChannels = React.useMemo(
    () => org_nodes.filter((n) => n.sales_channel),
    [org_nodes]
  )

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    return warehouses.filter((w) => {
      if (tab === "allocated" && w.sale_channel_node_ids.length === 0) return false
      if (channelFilter !== "all" && !w.sale_channel_node_ids.includes(channelFilter)) return false
      if (!q) return true
      return (
        w.name.toLowerCase().includes(q) ||
        (w.code ?? "").toLowerCase().includes(q) ||
        String(w.external_id).includes(q)
      )
    })
  }, [warehouses, query, tab, channelFilter])

  const allocatedCount = warehouses.filter((w) => w.sale_channel_node_ids.length > 0).length

  function handleSave(warehouse: Warehouse, nodeIds: string[]) {
    setSavingId(warehouse.id)
    router.patch(
      warehouse_path(market.slug, warehouse.id),
      { warehouse: { sale_channel_node_ids: nodeIds } },
      {
        preserveScroll: true,
        preserveState: true,
        onSuccess: () => {
          setWarehouses((prev) =>
            prev.map((w) => (w.id === warehouse.id ? { ...w, sale_channel_node_ids: nodeIds } : w))
          )
          router.reload({ only: ["warehouses"] })
        },
        onFinish: () => setSavingId(null),
      }
    )
  }

  return (
    <AppLayout
      header={
        <PageTitle
          title="Warehouses"
          subtitle="Map each warehouse to a channel, or leave unallocated"
        />
      }
      headerActions={can.create && <Button onClick={() => setCreateOpen(true)}>New warehouse</Button>}
    >
      <div className="space-y-4">
        <div className="p-4 bg-accent border border-border rounded-lg">
          <h2 className="text-sm font-semibold text-primary mb-1">About warehouse mapping</h2>
          <p className="text-sm text-primary leading-relaxed">
            Each warehouse holds physical stock for the sale channels it's assigned to below.
          </p>
          <p className="text-sm text-primary leading-relaxed mt-2">
            Pick every sale channel this warehouse's stock serves — a shared warehouse can serve
            several (e.g. Tiktok, Shopee, and Lazada). Warehouses serving no channel are not counted
            in any channel's stock.
          </p>
          <p className="text-sm text-primary leading-relaxed mt-2">
            Warehouses left <span className="font-medium">Unallocated</span> are not counted in any
            channel's plan. Use this for stock that doesn't belong to a sales channel — reserve
            stock, damage, warranty, transit, internal use, etc.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <ToggleGroup
            type="single"
            value={tab}
            onValueChange={(v) => v && setTab(v as typeof tab)}
          >
            <ToggleGroupItem value="allocated">Allocated ({allocatedCount})</ToggleGroupItem>
            <ToggleGroupItem value="all">All ({warehouses.length})</ToggleGroupItem>
          </ToggleGroup>
          <Input
            placeholder="Filter by name, code, or external ID"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="max-w-sm"
          />
          <Select value={channelFilter} onValueChange={setChannelFilter}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="All channels" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All channels</SelectItem>
              {salesChannels.map((node) => (
                <SelectItem key={node.id} value={node.id}>
                  {nodeLabel(node, nodesById)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="ml-auto text-sm text-muted-foreground">
            {filtered.length} of {warehouses.length}
          </span>
        </div>

        <div className="border border-border rounded-lg overflow-hidden bg-background">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">External ID</TableHead>
                <TableHead>Warehouse</TableHead>
                <TableHead className="w-32">Code</TableHead>
                <TableHead className="w-[28rem]">Serves</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((w) => (
                <WarehouseRow
                  key={w.id}
                  warehouse={w}
                  channels={salesChannels}
                  nodeLabel={(node) => nodeLabel(node, nodesById)}
                  saving={savingId === w.id}
                  onSave={handleSave}
                />
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                    No warehouses match.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <CreateWarehouseDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
      />
    </AppLayout>
  )
}
